import { useState, useRef, useLayoutEffect } from "react";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import { getTextByLang } from "../../utils/formUtils";
import { Box, IconButton, Typography } from "@mui/material";

function ExpandableText({ text, maxLines }) {
  const contentRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [needsToggle, setNeedsToggle] = useState(false);
  const [calculatedMaxHeight, setCalculatedMaxHeight] = useState(0);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const style = window.getComputedStyle(el);
    const lineHeightStr = style.lineHeight;
    let lineHeight = 20;

    if (lineHeightStr && lineHeightStr !== "normal") {
      lineHeight = parseFloat(lineHeightStr);
    } else {
      const fontSize = parseFloat(style.fontSize || "14");
      lineHeight = fontSize * 1.4;
    }

    const maxH = lineHeight * maxLines;
    setCalculatedMaxHeight(maxH);

    const prevMaxHeight = el.style.maxHeight;
    el.style.maxHeight = "none";
    const fullH = el.scrollHeight;
    el.style.maxHeight = prevMaxHeight;

    const threeLinesHeight = lineHeight * 3;
    setNeedsToggle(fullH > threeLinesHeight);
  }, [text, maxLines]);

  return (
    <Box position="relative" width="100%">
      <Box
        ref={contentRef}
        sx={{
          overflow: "hidden",
          maxHeight: isOpen ? "none" : `${calculatedMaxHeight}px`,
          transition: "max-height 300ms ease",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          lineHeight: 1.6,
        }}
      >
        <Typography variant="body1">{getTextByLang(text)}</Typography>
      </Box>

      {!isOpen && needsToggle && (
        <Box
          sx={{
            pointerEvents: "none",
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: `${Math.min(48, Math.round(calculatedMaxHeight * 0.4))}px`,
            background:
              "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)",
          }}
        />
      )}

      {needsToggle && (
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <IconButton
            size="small"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "collapse" : "expand"}
          >
            {isOpen ? <RemoveOutlinedIcon /> : <AddOutlinedIcon />}
          </IconButton>
        </Box>
      )}
    </Box>
  );
}

export default ExpandableText;
